/**
 * SaveViewDialog - Modal for saving current columns + filters as a view
 */

import { useState, useEffect, useRef } from 'react';
import type { View, CreateViewRequest, UpdateViewRequest, ViewFilter } from './types';

interface SaveViewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateViewRequest | UpdateViewRequest) => Promise<void>;

  // Current state to save
  siteId: string;
  contentType: string;
  columns: string[];
  filters: ViewFilter[];
  filterLogic: 'and' | 'or';
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';

  // If editing an existing view
  existingView?: View | null;

  // Loading state
  isSaving?: boolean;
}

// Predefined icons for views
const VIEW_ICONS = ['📋', '📁', '⭐', '🔥', '📊', '📈', '🎯', '✅', '⚡', '🔍', '📝', '💼'];

// Predefined colors for view badges
const VIEW_COLORS = [
  '#3B82F6', // blue
  '#10B981', // green
  '#F59E0B', // amber
  '#EF4444', // red
  '#8B5CF6', // purple
  '#EC4899', // pink
  '#06B6D4', // cyan
  '#F97316', // orange
];

export function SaveViewDialog({
  isOpen,
  onClose,
  onSave,
  siteId,
  contentType,
  columns,
  filters,
  filterLogic,
  sortColumn,
  sortDirection,
  existingView,
  isSaving = false,
}: SaveViewDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [color, setColor] = useState<string | null>(null);
  const [scope, setScope] = useState<'site' | 'org'>('site');
  const [visibility, setVisibility] = useState<'private' | 'shared'>('private');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize form when dialog opens or existingView changes
  useEffect(() => {
    if (isOpen) {
      if (existingView) {
        setName(existingView.name);
        setDescription(existingView.description || '');
        setIcon(existingView.icon);
        setColor(existingView.color);
        setScope(existingView.site_id ? 'site' : 'org');
        setVisibility(existingView.visibility);
        setIsDefault(existingView.is_default);
      } else {
        setName('');
        setDescription('');
        setIcon(null);
        setColor(null);
        setScope('site');
        setVisibility('private');
        setIsDefault(false);
      }
      setError(null);

      // Focus the name input after a short delay
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, existingView]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter a name for the view');
      return;
    }

    try {
      if (existingView) {
        // Update existing view
        const updateData: UpdateViewRequest = {
          name: name.trim(),
          description: description.trim() || null,
          icon,
          color,
          columns,
          filters,
          filterLogic,
          sortColumn,
          sortDirection,
          visibility,
          isDefault,
        };
        await onSave(updateData);
      } else {
        // Create new view
        const createData: CreateViewRequest = {
          siteId: scope === 'site' ? siteId : null,
          contentType,
          name: name.trim(),
          description: description.trim() || undefined,
          icon: icon || undefined,
          color: color || undefined,
          columns,
          filters,
          filterLogic,
          sortColumn: sortColumn || undefined,
          sortDirection,
          visibility,
          isDefault,
        };
        await onSave(createData);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save view');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {existingView ? 'Update View' : 'Save View'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 space-y-4">
            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {error}
              </div>
            )}

            {/* Name */}
            <div>
              <label htmlFor="view-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={inputRef}
                type="text"
                id="view-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Draft Posts, Low Stock"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={255}
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="view-description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                id="view-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                maxLength={1000}
              />
            </div>

            {/* Icon & Color row */}
            <div className="flex gap-4">
              {/* Icon selector */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <div className="flex flex-wrap gap-1">
                  {VIEW_ICONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setIcon(icon === emoji ? null : emoji)}
                      className={`w-8 h-8 rounded flex items-center justify-center text-lg hover:bg-gray-100 ${
                        icon === emoji ? 'bg-blue-100 ring-2 ring-blue-500' : ''
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>

              {/* Color selector */}
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <div className="flex flex-wrap gap-1">
                  {VIEW_COLORS.map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => setColor(color === hex ? null : hex)}
                      className={`w-6 h-6 rounded-full ${
                        color === hex ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                      }`}
                      style={{ backgroundColor: hex }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Scope (only for new views) */}
            {!existingView && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scope
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      checked={scope === 'site'}
                      onChange={() => setScope('site')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">This site only</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      checked={scope === 'org'}
                      onChange={() => setScope('org')}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Organization template</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {scope === 'site'
                    ? 'View will only appear for this site'
                    : 'View can be applied to any site in your organization'}
                </p>
              </div>
            )}

            {/* Visibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibility
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={visibility === 'private'}
                    onChange={() => setVisibility('private')}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Just me</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={visibility === 'shared'}
                    onChange={() => setVisibility('shared')}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Share with team</span>
                </label>
              </div>
            </div>

            {/* Default view checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Set as default view for {contentType}
              </span>
            </label>

            {/* Summary of what will be saved */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-xs font-medium text-gray-500 uppercase mb-2">
                View Configuration
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <div>
                  <span className="text-gray-500">Columns:</span>{' '}
                  {columns.length} selected
                </div>
                <div>
                  <span className="text-gray-500">Filters:</span>{' '}
                  {filters.length === 0 ? 'None' : `${filters.length} filter${filters.length > 1 ? 's' : ''}`}
                </div>
                {sortColumn && (
                  <div>
                    <span className="text-gray-500">Sort:</span>{' '}
                    {sortColumn} ({sortDirection})
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving && (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {existingView ? 'Update View' : 'Save View'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
